import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import { retryDeploymentJob, type DeploymentJob } from '../services/api'
import { useTaskList, taskKeys, type Task } from '../hooks/useTaskList'
import { useTaskListStore } from '../store/taskListStore'
import DeploymentJobsPanel from './DeploymentJobsPanel'
import PackagingJobRow from './PackagingJobRow'

/**
 * The task list itself: the tenant's running work first, then what finished and has not been
 * read. Deployment runs render through the same panel the deployments page uses, so a run looks
 * the same in both places and retry means the same thing in both.
 */
const TaskListPopover: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const list = useTaskList()
  const dismiss = useTaskListStore((s) => s.dismiss)
  const dismissMany = useTaskListStore((s) => s.dismissMany)
  const queryClient = useQueryClient()

  const [retryingId, setRetryingId] = useState<string | null>(null)
  const retry = useMutation({
    mutationFn: (job: DeploymentJob) => retryDeploymentJob(job.id),
    onMutate: (job: DeploymentJob) => setRetryingId(job.id),
    onSettled: () => {
      setRetryingId(null)
      // The deployment lists are re-read when the retried run finishes; see useDeploymentJobsQuery.
      queryClient.invalidateQueries({ queryKey: taskKeys.deploymentJobs() })
    },
  })

  const row = (task: Task) => {
    const finished = task.state !== 'running'
    if (task.kind === 'deployment') {
      return (
        <div key={task.id}>
          <DeploymentJobsPanel
            jobs={[task.job]}
            expandedByDefault={() => task.state === 'running'}
            onRetry={(job) => retry.mutate(job)}
            retryingId={retryingId}
            onDismiss={finished ? (job) => dismiss(job.id) : undefined}
          />
          <Link
            to="/deployments"
            onClick={onClose}
            className="mt-1 inline-block px-1 text-xs text-theme-accent-blue hover:underline"
          >
            Intune deployments
          </Link>
        </div>
      )
    }
    return (
      <PackagingJobRow
        key={task.id}
        job={task.job}
        onDismiss={finished ? (job) => dismiss(job.job_id) : undefined}
        onNavigate={onClose}
      />
    )
  }

  return (
    <div
      role="dialog"
      aria-label="Tasks"
      className="absolute right-0 mt-2 w-[28rem] max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto rounded-lg border border-theme-border-primary bg-theme-card shadow-lg z-50"
    >
      <div className="sticky top-0 flex items-center justify-between border-b border-theme-border-primary bg-theme-card px-4 py-2.5">
        <div>
          <p className="text-sm font-semibold text-theme-text-primary">Tasks</p>
          <p className="text-xs text-theme-text-secondary">
            {list.runningCount === 0
              ? 'Nothing running'
              : `${list.runningCount} running`}
          </p>
        </div>
        {list.finished.length > 0 && (
          <button
            type="button"
            onClick={() => dismissMany(list.finished.map((t) => t.id))}
            className="rounded-lg border border-theme-border-primary px-2.5 py-1 text-xs text-theme-text-primary hover:bg-theme-tertiary"
          >
            Clear finished
          </button>
        )}
      </div>

      <div className="space-y-3 px-4 py-3">
        {list.error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <p className="text-xs text-theme-text-secondary">
              Could not refresh. Showing the last successful read. {list.error}
            </p>
          </div>
        )}

        {list.isLoading && !list.tasks.length && (
          <p className="text-sm text-theme-text-tertiary">Loading…</p>
        )}

        {!list.isLoading && !list.tasks.length && !list.error && (
          <div className="rounded-lg border border-dashed border-theme-border-primary px-4 py-6">
            <p className="text-sm text-theme-text-secondary">Nothing to show.</p>
            <p className="mt-1 text-xs text-theme-text-tertiary">
              Deployments and packaging jobs appear here while they run and until they are dismissed.
            </p>
          </div>
        )}

        {list.tasks.map(row)}
      </div>

      {(list.hiddenCount > 0 || list.tasks.length > 0) && (
        <div className="flex items-center justify-between border-t border-theme-border-primary px-4 py-2 text-xs text-theme-text-tertiary">
          <span>{list.hiddenCount > 0 ? `${list.hiddenCount} older not shown` : 'Full history:'}</span>
          <span className="flex gap-3">
            {list.showsDeployments && (
              <Link to="/deployments" onClick={onClose} className="text-theme-accent-blue hover:underline">
                Intune deployments
              </Link>
            )}
            {list.showsPackaging && (
              <Link to="/jobs" onClick={onClose} className="text-theme-accent-blue hover:underline">
                Background jobs
              </Link>
            )}
          </span>
        </div>
      )}
    </div>
  )
}

export default TaskListPopover
